<template>
  <div class="binding-confirm">
    <!-- 待确认绑定申请 -->
    <div class="section">
      <div class="section-title">待确认的绑定申请</div>
      <div v-if="pendingRequests.length === 0" class="empty-tip">
        暂无绑定申请
      </div>
      <div
        v-for="item in pendingRequests"
        :key="item.binding_id"
        class="bind-card"
      >
        <div class="bind-card-top">
          <div class="bind-card-identity">
            <span class="nickname">{{
              item.parent_nickname || getRelationText(item.relation)
            }}</span>
            <span class="relation-tag">{{
              getRelationText(item.relation)
            }}</span>
          </div>
          <div class="bind-card-meta">
            <span class="request-time"
              >{{ formatTime(item.created_at) }} 申请</span
            >
            <span v-if="item._remaining" class="expire-tip">{{
              item._remaining
            }}</span>
          </div>
        </div>
        <div class="bind-card-actions">
          <el-button
            type="danger"
            size="small"
            plain
            :loading="item._rejecting"
            :disabled="item._expired"
            @click="handleReject(item)"
            >拒绝</el-button
          >
          <el-button
            type="success"
            size="small"
            :loading="item._confirming"
            :disabled="item._expired"
            @click="handleConfirm(item)"
            >同意绑定</el-button
          >
        </div>
      </div>
    </div>

    <!-- 已绑定的家长 -->
    <div class="section">
      <div class="section-title">已绑定的家长</div>
      <div v-if="boundParents.length === 0" class="empty-tip">
        暂无已绑定的家长
      </div>
      <div
        v-for="item in boundParents"
        :key="item.binding_id"
        class="bind-item"
      >
        <div class="bind-info">
          <span class="nickname">{{
            item.parent_nickname || item.nickname
          }}</span>
          <span class="relation">（{{ getRelationText(item.relation) }}）</span>
        </div>
        <div class="bind-actions">
          <el-button
            type="text"
            size="small"
            :loading="item._unbinding"
            @click="handleUnbind(item)"
            >解绑</el-button
          >
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  getBindRequests,
  confirmBind,
  getBoundParents,
  unbindParent,
} from "@saas/service-api/src/parent-binding";

const RELATION_MAP = {
  1: "爸爸",
  2: "妈妈",
  3: "其他",
};

export default {
  name: "BindingConfirm",
  data() {
    return {
      pendingRequests: [],
      boundParents: [],
    };
  },
  mounted() {
    this.fetchData();
    this.countdownTimer = setInterval(() => {
      this.refreshCountdowns();
    }, 60000);
  },
  beforeDestroy() {
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }
  },
  methods: {
    getRelationText(relation) {
      return RELATION_MAP[relation] || "其他";
    },
    formatTime(ts) {
      if (!ts) return "";
      const d = new Date(ts * 1000);
      const pad = (n) => String(n).padStart(2, "0");
      return `${d.getMonth() + 1}/${d.getDate()} ${pad(d.getHours())}:${pad(
        d.getMinutes()
      )}`;
    },
    calcRemaining(expiredAt) {
      if (!expiredAt) return "";
      const now = Math.floor(Date.now() / 1000);
      const diff = expiredAt - now;
      if (diff <= 0) return "已过期";
      const hours = Math.floor(diff / 3600);
      const mins = Math.floor((diff % 3600) / 60);
      return `剩余 ${hours}小时${mins}分钟`;
    },
    fetchData() {
      this.fetchPendingRequests();
      this.fetchBoundParents();
    },
    refreshCountdowns() {
      this.pendingRequests.forEach((item) => {
        item._remaining = this.calcRemaining(item.expired_at);
        item._expired = this.isExpired(item.expired_at);
      });
    },
    isExpired(expiredAt) {
      if (!expiredAt) return false;
      return Math.floor(Date.now() / 1000) >= expiredAt;
    },
    fetchPendingRequests() {
      getBindRequests()
        .then((res) => {
          if (res && res.code === 1) {
            this.pendingRequests = (res.data || []).map((item) => ({
              ...item,
              _confirming: false,
              _rejecting: false,
              _remaining: this.calcRemaining(item.expired_at),
              _expired: this.isExpired(item.expired_at),
            }));
          }
        })
        .catch(() => {});
    },
    fetchBoundParents() {
      getBoundParents()
        .then((res) => {
          if (res && res.code === 1) {
            this.boundParents = (res.data || []).map((item) => ({
              ...item,
              _unbinding: false,
            }));
          }
        })
        .catch(() => {});
    },
    handleConfirm(item) {
      item._confirming = true;
      confirmBind({ binding_id: item.binding_id, accept: true })
        .then((res) => {
          if (res && res.code === 1) {
            this.$message.success("已同意绑定申请");
            this.fetchData();
          } else {
            this.$message.error(res?.message || "操作失败");
          }
        })
        .catch(() => {
          this.$message.error("操作失败，请重试");
        })
        .finally(() => {
          item._confirming = false;
        });
    },
    handleReject(item) {
      item._rejecting = true;
      confirmBind({ binding_id: item.binding_id, accept: false })
        .then((res) => {
          if (res && res.code === 1) {
            this.$message.success("已拒绝绑定申请");
            this.fetchPendingRequests();
          } else {
            this.$message.error(res?.message || "操作失败");
          }
        })
        .catch(() => {
          this.$message.error("操作失败，请重试");
        })
        .finally(() => {
          item._rejecting = false;
        });
    },
    handleUnbind(item) {
      this.$confirm("确认解绑该家长吗？", "提示", {
        confirmButtonText: "确定",
        cancelButtonText: "取消",
        type: "warning",
      })
        .then(() => {
          item._unbinding = true;
          return unbindParent(item.binding_id);
        })
        .then((res) => {
          if (res && res.code === 1) {
            this.$message.success("解绑成功");
            this.fetchBoundParents();
          } else {
            this.$message.error(res?.message || "解绑失败");
          }
        })
        .catch((err) => {
          if (err === "cancel") return;
          this.$message.error("操作失败，请重试");
        })
        .finally(() => {
          item._unbinding = false;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.binding-confirm {
  padding: 20px;

  .section {
    margin-bottom: 32px;

    .section-title {
      font-size: 16px;
      font-weight: 600;
      color: var(--text-primary, #0d1400);
      margin-bottom: 12px;
      padding-bottom: 8px;
      border-bottom: 1px solid var(--border-color, #f2f4f5);
    }

    .empty-tip {
      font-size: 14px;
      color: var(--text-secondary, #acacac);
      text-align: center;
      padding: 24px 0;
    }

    .bind-item {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 12px 0;
      border-bottom: 1px solid var(--border-color, #f2f4f5);

      &:last-child {
        border-bottom: none;
      }

      .bind-info {
        display: flex;
        align-items: center;

        .nickname {
          font-size: 14px;
          color: var(--text-primary, #0d1400);
          font-weight: 500;
        }

        .relation {
          font-size: 13px;
          color: var(--text-secondary, #515350);
          margin-left: 4px;
        }
      }

      .bind-actions {
        display: flex;
        gap: 8px;
      }
    }

    .bind-card {
      background: var(--input-bg, #fafbfc);
      border: 1px solid var(--border-color, #f2f4f5);
      border-radius: 8px;
      padding: 16px;
      margin-bottom: 12px;

      .bind-card-top {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        margin-bottom: 12px;
      }

      .bind-card-identity {
        display: flex;
        align-items: center;
        gap: 8px;

        .nickname {
          font-size: 15px;
          color: var(--text-primary, #0d1400);
          font-weight: 600;
        }

        .relation-tag {
          font-size: 12px;
          color: var(--text-secondary, #515350);
          background: var(--disabled-bg, #eef0f2);
          padding: 2px 8px;
          border-radius: 4px;
        }
      }

      .bind-card-meta {
        display: flex;
        align-items: center;
        gap: 12px;

        .request-time {
          font-size: 12px;
          color: var(--text-secondary, #acacac);
        }

        .expire-tip {
          font-size: 12px;
          color: #f0ad4e;
          font-weight: 500;
        }
      }

      .bind-card-actions {
        display: flex;
        justify-content: flex-end;
        gap: 8px;
        padding-top: 12px;
        border-top: 1px solid var(--border-color, #f2f4f5);
      }
    }
  }
}
</style>
